/**
 * Transactions — entry, filtering, re-categorization, edit, delete.
 *
 * The entry form sits at the top because keying receipts is the daily job. The
 * list below is both the record and the correction surface: every inline category
 * change teaches the rules that pre-fill the next entry.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { BudgetCategory, Transaction } from '../../shared/types';
import { api, money } from '../../lib/api';
import { currentPeriod, isValidPeriod, periodLabel } from './dates';
import QuickAddForm from './QuickAddForm';
import TransactionTable, { type SortDir, type SortKey } from './TransactionTable';
import { EMPTY_SUMMARY, fetchSummary, type TransactionSummary } from './summary';
import {
  Card,
  EmptyState,
  ErrorState,
  Flash,
  LoadingRows,
  PageHeader,
  PeriodPicker,
  inputClass,
  quietButtonClass,
} from './ui';

/** Recent history across all months, for merchant + account autocomplete. */
const HISTORY_LIMIT = 300;
/** Rows fetched at a time. The count on screen comes from /summary, never from this page. */
const PAGE_SIZE = 200;

export default function TransactionsPage() {
  const [params, setParams] = useSearchParams();

  const periodParam = params.get('period');
  const period = isValidPeriod(periodParam) ? periodParam : currentPeriod();
  const categoryParam = params.get('category_id');
  const uncategorizedOnly = params.get('uncategorized') === '1';
  const searchParam = params.get('q') ?? '';

  // Search is debounced into the URL (?q=) so it filters server-side over the whole
  // month, not just the rows already on screen. Sort is a view preference over the
  // loaded rows, so it lives in local state and never refetches.
  const [search, setSearch] = useState(searchParam);
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const [categories, setCategories] = useState<BudgetCategory[] | null>(null);
  const [rows, setRows] = useState<Transaction[] | null>(null);
  const [summary, setSummary] = useState<TransactionSummary | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [history, setHistory] = useState<Transaction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<number | null>(null);

  const say = useCallback((message: string) => {
    setFlash(message);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 4000);
  }, []);

  useEffect(() => () => { if (flashTimer.current) window.clearTimeout(flashTimer.current); }, []);

  const query = useMemo(() => {
    const q = new URLSearchParams({ period });
    if (uncategorizedOnly) q.set('uncategorized', '1');
    else if (categoryParam) q.set('category_id', categoryParam);
    if (searchParam.trim()) q.set('q', searchParam.trim());
    return q.toString();
  }, [period, categoryParam, uncategorizedOnly, searchParam]);

  // Keep the box in sync when the URL's q changes elsewhere (e.g. Clear filters).
  useEffect(() => {
    setSearch(searchParam);
  }, [searchParam]);

  // Debounce typing into ?q= so we're not refetching on every keystroke.
  useEffect(() => {
    if (search === searchParam) return;
    const id = window.setTimeout(() => setFilter({ q: search }), 350);
    return () => window.clearTimeout(id);
    // setFilter is stable enough here; only the typed value should drive this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // A new filter is a new set of rows — start back at one page of them.
  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [query]);

  /** The count and the total are the server's, over the whole filter — never this page's. */
  const refreshSummary = useCallback(() => {
    fetchSummary(query)
      .then(setSummary)
      .catch(() => {
        /* the list still stands; the header just falls back to "…" */
      });
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cats, list, totals, recent] = await Promise.all([
        api.get<BudgetCategory[]>('/categories'),
        api.get<Transaction[]>(`/transactions?${query}&limit=${limit}`),
        fetchSummary(query),
        api.get<Transaction[]>(`/transactions?limit=${HISTORY_LIMIT}`),
      ]);
      setCategories(cats);
      setRows(list);
      setSummary(totals);
      setHistory(recent);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }, [query, limit]);

  useEffect(() => {
    void load();
  }, [load]);

  function setFilter(next: {
    period?: string;
    category_id?: string | null;
    uncategorized?: boolean;
    q?: string;
  }) {
    const p = new URLSearchParams(params);
    if (next.period) p.set('period', next.period);
    if (next.uncategorized !== undefined) {
      if (next.uncategorized) {
        p.set('uncategorized', '1');
        p.delete('category_id');
      } else {
        p.delete('uncategorized');
      }
    }
    if (next.category_id !== undefined) {
      if (next.category_id) {
        p.set('category_id', next.category_id);
        p.delete('uncategorized');
      } else {
        p.delete('category_id');
      }
    }
    if (next.q !== undefined) {
      if (next.q.trim()) p.set('q', next.q.trim());
      else p.delete('q');
    }
    setParams(p, { replace: true });
  }

  function onSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'description' || key === 'category' ? 'asc' : 'desc');
    }
  }

  /** A new entry only belongs in the visible list if it passes the active filter. */
  function matchesFilter(t: Transaction): boolean {
    if (t.occurred_on.slice(0, 7) !== period) return false;
    const s = searchParam.trim().toLowerCase();
    if (s && !t.description.toLowerCase().includes(s)) return false;
    if (uncategorizedOnly) return t.category_id === null;
    if (categoryParam) return t.category_id === Number(categoryParam);
    return true;
  }

  function handleAdded(t: Transaction) {
    setHistory((prev) => [t, ...prev]);
    refreshSummary();
    if (!matchesFilter(t)) {
      say(
        t.occurred_on.slice(0, 7) !== period
          ? `Saved to ${periodLabel(t.occurred_on.slice(0, 7))}`
          : 'Saved — hidden by the current filter'
      );
      return;
    }
    setRows((prev) =>
      [t, ...(prev ?? [])].sort(
        (a, b) => b.occurred_on.localeCompare(a.occurred_on) || b.id - a.id
      )
    );
  }

  function handleChanged(t: Transaction) {
    setHistory((prev) => prev.map((x) => (x.id === t.id ? t : x)));
    setRows((prev) => {
      if (!prev) return prev;
      // Re-categorizing out of a filtered view drops the row from that view.
      if (!matchesFilter(t)) return prev.filter((x) => x.id !== t.id);
      return prev.map((x) => (x.id === t.id ? t : x));
    });
    refreshSummary();
  }

  function handleDeleted(t: Transaction) {
    setHistory((prev) => prev.filter((x) => x.id !== t.id));
    setRows((prev) => (prev ? prev.filter((x) => x.id !== t.id) : prev));
    refreshSummary();
  }

  // The month's count and total come from the server's aggregate over every row in
  // the filter. Adding up `rows` here would be wrong the moment the month has more
  // transactions than one page holds.
  const totals = summary ?? EMPTY_SUMMARY;
  const truncated = !!rows && !!summary && summary.count > rows.length;
  const filtered = uncategorizedOnly || !!categoryParam || !!searchParam.trim();

  const sortedRows = useMemo(() => {
    if (!rows) return null;
    const nameOf = (t: Transaction) =>
      categories?.find((c) => c.id === t.category_id)?.name ?? '';
    const arr = [...rows];
    arr.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'date':
          cmp = a.occurred_on.localeCompare(b.occurred_on) || a.id - b.id;
          break;
        case 'amount':
          cmp = a.amount - b.amount;
          break;
        case 'description':
          cmp = a.description.localeCompare(b.description, undefined, { sensitivity: 'base' });
          break;
        case 'category':
          cmp = nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: 'base' });
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [rows, categories, sortKey, sortDir]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Transactions"
        subtitle={`Keyed by hand — ${periodLabel(period)}`}
        action={
          <Link to="/money" className={quietButtonClass}>
            Back to budget
          </Link>
        }
      />

      {error && !loading && <ErrorState message={error} onRetry={() => void load()} />}

      {categories && (
        <QuickAddForm
          categories={categories}
          history={history}
          onAdded={handleAdded}
          onUndone={handleDeleted}
          onError={say}
        />
      )}

      <Card className="p-4">
        <div className="flex flex-col gap-3">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search descriptions…"
            aria-label="Search transactions by description"
            className={inputClass}
          />
          <div className="flex flex-wrap items-center gap-3">
            <PeriodPicker period={period} onChange={(p) => setFilter({ period: p })} />

          <div>
            <label className="sr-only" htmlFor="filter-category">
              Filter by category
            </label>
            <select
              id="filter-category"
              value={uncategorizedOnly ? '' : (categoryParam ?? 'all')}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'all') setFilter({ category_id: null, uncategorized: false, q: '' });
                else setFilter({ category_id: v, uncategorized: false });
              }}
              className="min-h-[44px] rounded-xl border border-border bg-surface px-3 py-2 text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/40"
            >
              <option value="all">All categories</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-xl border border-border bg-surface px-3 py-2 text-sm text-ink has-[:checked]:border-honey has-[:checked]:bg-honey-soft">
            <input
              type="checkbox"
              checked={uncategorizedOnly}
              onChange={(e) => setFilter({ uncategorized: e.target.checked })}
              className="h-4 w-4 accent-[color:var(--color-honey)] focus:outline-none focus-visible:ring-2 focus-visible:ring-sage/40"
            />
            Needs a category only
          </label>

          {filtered && (
            <button
              type="button"
              onClick={() => setFilter({ category_id: null, uncategorized: false, q: '' })}
              className="min-h-[44px] px-2 text-sm text-ink-soft underline underline-offset-2 hover:text-ink"
            >
              Clear filters
            </button>
          )}

          <p className="ml-auto text-sm text-ink-soft">
            {summary ? (
              <>
                <span className="font-medium text-ink">{totals.count}</span>{' '}
                {totals.count === 1 ? 'transaction' : 'transactions'} ·{' '}
                <span className="font-medium tabular-nums text-ink">{money(totals.total)}</span>
                {totals.uncategorized_count > 0 && !uncategorizedOnly && (
                  <>
                    {' '}
                    ·{' '}
                    <span className="text-near">
                      {totals.uncategorized_count} need a category
                    </span>
                  </>
                )}
              </>
            ) : (
              'Loading…'
            )}
          </p>
          </div>
        </div>
      </Card>

      {loading && !rows && <LoadingRows />}

      {rows && rows.length === 0 && (
        <EmptyState
          title={filtered ? 'Nothing matches these filters' : `Nothing logged for ${periodLabel(period)}`}
          body={
            filtered
              ? 'Try another category or month — or clear the filters to see everything in this month.'
              : 'Add the first one above. Type the merchant, the amount, and hit Enter — the category will fill itself in from then on.'
          }
          action={
            filtered ? (
              <button
                type="button"
                onClick={() => setFilter({ category_id: null, uncategorized: false, q: '' })}
                className={quietButtonClass}
              >
                Clear filters
              </button>
            ) : undefined
          }
        />
      )}

      {sortedRows && sortedRows.length > 0 && categories && (
        <TransactionTable
          rows={sortedRows}
          categories={categories}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          onChanged={handleChanged}
          onDeleted={handleDeleted}
          onFlash={say}
          onError={say}
        />
      )}

      {/* The list is paged; the count and total above are not. Say so, and offer the rest. */}
      {truncated && (
        <div className="flex flex-col items-center gap-2 py-2">
          <p className="text-sm text-ink-soft">
            Showing the {rows!.length} most recent of {totals.count}.
          </p>
          <button
            type="button"
            onClick={() => setLimit((n) => n + PAGE_SIZE)}
            disabled={loading}
            className={quietButtonClass}
          >
            {loading ? 'Loading…' : `Show ${Math.min(PAGE_SIZE, totals.count - rows!.length)} more`}
          </button>
        </div>
      )}

      <Flash message={flash} />
    </div>
  );
}
